var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __read = this && this.__read || function (o, n) {
  var m = typeof Symbol === "function" && o[Symbol.iterator];
  if (!m) return o;
  var i = m.call(o),
    r,
    ar = [],
    e;
  try {
    while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
  } catch (error) {
    e = {
      error: error
    };
  } finally {
    try {
      if (r && !r.done && (m = i["return"])) m.call(i);
    } finally {
      if (e) throw e.error;
    }
  }
  return ar;
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import React from 'react';
import queryString from 'query-string';
import { pickBy, mapValues, map, includes, sortBy, omit, filter, kebabCase, reduce, camelCase } from 'lodash';
import { useAppState } from './use-app-state';
import { ViewConfigDefault, ViewContext } from '../context/view-context';
import { useReady } from './use-ready';
export var ViewDefaultState = {
  view: '',
  types: [],
  selectedTypes: [],
  filters: {}
};
export function useView(key, defaultView, defaultSubView, options) {
  // todo - wire this to hash state instead
  var _a = __read(useAppState(), 3),
    appState = _a[0],
    setAppState = _a[1],
    patchAppState = _a[2];
  var state = appState[key] || ViewDefaultState;
  var context = React.useContext(ViewContext) || ViewConfigDefault;
  var debug = context.debug || (options === null || options === void 0 ? void 0 : options.debug) || false;
  var filterStateVariant = context.filterStateVariant || 'url';
  var isReady = useReady();
  var _b = __read(React.useState(''), 2),
    hash = _b[0],
    setHash = _b[1];
  React.useEffect(function () {
    var handleHashChange = function () {
      if (typeof window !== 'undefined') {
        // setting hash 
        setHash(window.location.hash);
      }
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('hashchange', handleHashChange);
      return function () {
        return window.removeEventListener('hashchange', handleHashChange);
      };
    }
  }, []);
  React.useEffect(function () {
    if (isReady) {
      setHash(window.location.hash);
    }
  }, [isReady]);
  var types = state.types || [];
  var canonicalTypes = types.join(',');
  var canonicalFilters = queryString.stringify(state.filters);
  var selectedTypes = state.selectedTypes || [];
  var deps = filterStateVariant === 'state' ? [canonicalFilters, types, state.view, isReady] : [canonicalTypes, hash, isReady];
  var _c = React.useMemo(function () {
      var parts = queryString.parse(hash.slice(1).replace(/;/g, '&'));
      if (debug) {
        console.log("useView: QUERY STRING PARTS", parts);
      }
      var setActiveView = function (view, suffix) {
        var currentParts = queryString.parse(hash.slice(1).replace(/;/g, '&'));
        var newParts = {
          view: view
        };
        // this causes bugs when switching between views
        // currentParts.view = view;
        var newHash = queryString.stringify(newParts).replace(/&/g, ';');
        var finalHash = newHash + (suffix || '');
        window.location.hash = finalHash;
      };
      var setActiveSubView = function (subview) {
        var currentParts = queryString.parse(hash.slice(1).replace(/;/g, '&'));
        currentParts.subview = subview;
        var newHash = queryString.stringify(currentParts).replace(/&/g, ';');
        window.location.hash = newHash;
      };
      var activeView = isReady && parts.view || defaultView;
      var activeSubView = isReady && parts.subview || defaultSubView;
      var showView = function (view) {
        return !isReady || activeView === view;
      };
      var showSubView = function (view) {
        return !isReady || activeSubView === view;
      };
      var filters = filterStateVariant === 'state' ? state.filters[activeView] || {} : isReady && reduce(omit(parts, 'view'), function (acc, v, k) {
        if (k && (v === null || v === void 0 ? void 0 : v.split)) {
          acc[camelCase(k)] = v.split && v.split(',') || [];
        }
        return acc;
      }, {});
      if (debug) {
        console.log("useView: HASH ACTIVE FILTERS", filters);
      }
      var activeQueryFilters = mapValues(pickBy(filters, function (v, k) {
        return !!v.length;
      }), function (v, k) {
        return {
          in: v
        };
      });
      var setTypes = function (selectedTypes, types) {
        var _a;
        patchAppState((_a = {}, _a[key] = __assign(__assign({}, state), {
          selectedTypes: selectedTypes,
          types: types
        }), _a));
      };
      var setFilters = function (newFilters) {
        var _a, _b;
        if (filterStateVariant === 'url') {
          var hashParts = __spreadArray([parts.view && "view=".concat(parts.view)], __read(map(pickBy(newFilters, function (filter) {
            return filter === null || filter === void 0 ? void 0 : filter.length;
          }), function (v, k) {
            return "".concat(kebabCase(k), "=").concat(v.join && v.join(","));
          })), false);
          var hash_1 = filter(hashParts, function (part) {
            return part === null || part === void 0 ? void 0 : part.length;
          }).join("&");
          if (debug) {
            console.log("useView: setFilters hash", hash_1);
          }
          document.location.hash = "#".concat(hash_1);
        } else {
          var newFiltersState = __assign(__assign({}, filters), newFilters || {});
          if (debug) {
            console.log("useView: setFilters new state", newFiltersState);
          }
          patchAppState((_a = {}, _a[key] = {
            filters: __assign(__assign({}, state.filters), (_b = {}, _b[activeView] = newFiltersState, _b))
          }, _a));
        }
      };
      var onEntityFilterChange = function (fieldName, items) {
        var _a;
        setFilters(__assign(__assign({}, filters), (_a = {}, _a[fieldName] = items.map(function (item) {
          return item.value;
        }), _a)));
      };
      var resultsFor = function (args) {
        var type = args.type,
          data = args.data,
          by = args.by;
        var preparedResults = by ? sortBy(data || [], by) : data || [];
        return types.length === 0 || includes(types, type) ? preparedResults : [];
      };
      return {
        resultsFor: resultsFor,
        activeView: activeView,
        showView: showView,
        showSubView: showSubView,
        activeSubView: activeSubView,
        setActiveView: setActiveView,
        setActiveSubView: setActiveSubView,
        setTypes: setTypes,
        setFilters: setFilters,
        onEntityFilterChange: onEntityFilterChange,
        filters: filters,
        activeQueryFilters: activeQueryFilters
      };
    }, deps),
    resultsFor = _c.resultsFor,
    showView = _c.showView,
    showSubView = _c.showSubView,
    activeView = _c.activeView,
    activeSubView = _c.activeSubView,
    setActiveView = _c.setActiveView,
    setActiveSubView = _c.setActiveSubView,
    setTypes = _c.setTypes,
    setFilters = _c.setFilters,
    onEntityFilterChange = _c.onEntityFilterChange,
    filters = _c.filters,
    activeQueryFilters = _c.activeQueryFilters;
  return {
    state: state,
    activeView: activeView,
    activeSubView: activeSubView,
    types: types,
    showView: showView,
    showSubView: showSubView,
    isActiveView: showView,
    selectedTypes: selectedTypes,
    resultsFor: resultsFor,
    filters: filters,
    activeQueryFilters: activeQueryFilters,
    setActiveView: setActiveView,
    setActiveSubView: setActiveSubView,
    setTypes: setTypes,
    setFilters: setFilters,
    onEntityFilterChange: onEntityFilterChange
  };
}